import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Resizer from "react-image-file-resizer";
import { toast } from "sonner";

import {
  getHouseProfile,
  getShovellerProfile,
  updateHomeOwnerProfile,
  updateShovellerProfile,
} from "../../../apiManager/houseOwner/getHouseProfile";
import { FaExclamationTriangle, FaCreditCard } from "react-icons/fa";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Avatar, AvatarImage } from "../../ui/avatar";
import {
  Edit,
  Save,
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Star,
  LogOut,
  ClipboardList,
  CheckCircle,
  DollarSign,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { Loader } from "../../base/Loader";
import { logout } from "../../../slices/authSlice";
import { LoginBox } from "../shared/Login";
import { Authenticate } from "./Authenticate";
import { SignupWizard as HomeownerSignupWizard } from "../homeowner/profile/SignupWizard";
import { SignupWizard as HelperSignupWizard } from "../helper/profile/SignupWizard";
import { Step5 } from "../helper/profile/Step5";

export function ProfileTab({ user }) {
  const navigate = useNavigate();
  const [paymentView, setPaymentView] = useState(false);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const userId = userInfo ? userInfo.user.id : null;
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    userName: "",
    email: "",
    phoneNumber: "",
    address: "",
    city: "",
    province: "",
    country: "",
    postalCode: "",
    imageUrl: "",
  });
  const [selectedImage, setSelectedImage] = useState(null);

  // Stats (could be fetched from API too)
  const stats = [
    { icon: <ClipboardList className="w-8 h-8" />, value: "12", label: "Jobs Posted" },
    { icon: <CheckCircle className="w-8 h-8 text-green-600" />, value: "10", label: "Completed" },
    { icon: <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />, value: "4.8", label: "Rating" },
    { icon: <DollarSign className="w-8 h-8 text-gray-700" />, value: "$420", label: "Total Spent" },
  ];

  // Allowed image types
  const validImageTypes = ["image/jpeg", "image/png"];

  // Handle avatar change & resize
  const handleImageFileChange = (event) => {
    const file = event.target.files[0];
    if (file && validImageTypes.includes(file.type)) {
      Resizer.imageFileResizer(
        file,
        200,
        200,
        file.type === "image/jpeg" ? "JPEG" : "PNG",
        100,
        0,
        (uri) => setSelectedImage(uri),
        "base64"
      );
    } else {
      setSelectedImage(null);
      toast.error("Invalid file type. Please upload a JPEG or PNG image.");
    }
  };

  // Fetch existing user profile
  const fetchProfileData = async () => {
    setLoading(true);
    try {
      const res = await (user === "homeowner" ? getHouseProfile(userId) : getShovellerProfile(userId));
      if (res?.result) {
        const user = res.result;
        setProfile({
          userName: user.userName || "",
          email: user.email || "",
          phoneNumber: user.phone || "",
          address: user.address || "",
          city: user.city || "",
          province: user.province || "",
          country: user.country || "",
          postalCode: user.postalCode || "",
          imageUrl: user.imageUrl || "",
        });
        setSelectedImage(user.imageUrl || null);
      } else {
        toast.error(res.message || "Data not found");
      }
    } catch (error) {
      toast.error(error.message || "Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProfileData();
    }
  }, [userId]);

  // Validate fields
  const validateFields = () => {
    if (!profile.userName.trim()) return "User Name is required";
    if (!profile.email.trim()) return "Email is required";
    if (!profile.phoneNumber.trim()) return "Phone Number is required";
    if (!profile.address.trim()) return "Address is required";
    if (!profile.city.trim()) return "City is required";
    if (!profile.province.trim()) return "Province is required";
    if (!profile.country.trim()) return "Country is required";
    if (!profile.postalCode.trim()) return "Postal Code is required";
    return "";
  };

  // Save updated profile
  const handleSave = async () => {
    const error = validateFields();
    if (error) {
      toast.error(error);
      return;
    }

    const data = new FormData();
    data.append("userName", profile.userName);
    data.append("email", profile.email);
    data.append("phoneNumber", profile.phoneNumber);
    data.append("address", profile.address);
    data.append("city", profile.city);
    data.append("province", profile.province);
    data.append("postalCode", profile.postalCode);
    data.append("country", profile.country);

    if (selectedImage && selectedImage !== profile.imageUrl) {
      data.append("image", selectedImage);
    }

    setLoading(true);
    try {
      const res = await (user === "homeowner" ? updateHomeOwnerProfile(userId, data) : updateShovellerProfile(userId, data));
      if (res?.data) {
        toast.success("Profile updated successfully!");
        setIsEditing(false);
        fetchProfileData(); // refresh after save
      } else {
        toast.error(res.message || "Failed to update profile");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    fetchProfileData(); // reset to original data
    setIsEditing(false);
  };

  const handleStripeSetup = () => {
    setPaymentView(true);
  }

  if (loading) return <Loader />;

  console.log("user id", userId);

  if (!userId) {
    return user === "homeowner" ? <Authenticate SignUpComponent={HomeownerSignupWizard} /> : <Authenticate defaultView='signup' SignUpComponent={HelperSignupWizard} />;
  }

  const isNotAuthorized = userInfo?.user.role === "shoveller" && (userInfo?.user.stripeAccountId === "none" || userInfo?.user.chargesEnabled === false);

  if (paymentView) {
    return (
      <div className="max-w-2xl mx-auto">
        <Step5 />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-12 px-4 sm:px-6 lg:px-8">
      {/* Page Title */}
      <div className="mt-8 lg:mt-0 py-3 text-center space-y-3">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black">
          Your Profile
        </h1>
        <p className="text-base sm:text-lg lg:text-xl text-gray-600">
          Manage your account information and preferences
        </p>

        {/* Stripe Authorization Warning */}
        {isNotAuthorized && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-2">
              <FaExclamationTriangle className="w-5 h-5 text-red-600" />
              <p className="text-red-700 font-medium text-sm">
                Your payment method hasn't been authorized
              </p>
            </div>
            <p className="text-red-600 text-sm mb-3">
              You need to connect to Stripe first to receive payments for your services.
            </p>
            <Button
              onClick={handleStripeSetup} // You'll need to implement this function
              className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 text-sm"
            >
              <FaCreditCard className="w-4 h-4 mr-2" />
              Authorize Payment
            </Button>
          </div>
        )}
      </div>

      {/* Profile Header */}
      <Card className="p-6 sm:p-8">
        <CardHeader className="p-0">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Avatar & Info */}
            <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8 w-full">
              <div className="relative">
                <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-gray-100">
                  {selectedImage ? (
                    <AvatarImage src={selectedImage} />
                  ) : (
                    <AvatarImage src="https://cdn-icons-png.flaticon.com/512/149/149071.png" />
                  )}
                </Avatar>
                {isEditing && (
                  <input
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    onChange={handleImageFileChange}
                  />
                )}
              </div>

              <div className="space-y-2 text-center sm:text-left">
                <CardTitle className="text-2xl sm:text-3xl lg:text-4xl">
                  {profile.userName}
                </CardTitle>
                <p className="text-gray-600 text-sm sm:text-lg">{profile.email}</p>
                {user !== "homeowner" && <p className="text-sm sm:text-lg text-primary">Referral Code: {userInfo.user.referralCode || ''}</p>}
                <div className="flex items-center justify-center sm:justify-start gap-2 mt-2">
                  <Star className="w-5 h-5 sm:w-6 sm:h-6 fill-yellow-400 text-yellow-400" />
                  <span className="text-lg sm:text-2xl font-semibold">4.8</span>
                  <span className="text-sm sm:text-lg text-gray-500">(24 reviews)</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col items-stretch gap-4 w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant={isEditing ? "default" : "outline"}
                  onClick={() => (isEditing ? handleSave() : setIsEditing(true))}
                  className={`w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg ${isEditing ? "bg-black text-white" : ""
                    }`}
                  size="lg"
                >
                  {isEditing ? (
                    <Save className="w-5 h-5 mr-2" />
                  ) : (
                    <Edit className="w-5 h-5 mr-2" />
                  )}
                  {isEditing ? "Save Changes" : "Edit Profile"}
                </Button>
              </div>

              {/* Logout */}
              {!isEditing && <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="w-full sm:w-auto px-6 py-3 text-base sm:text-lg"
                  >
                    <LogOut className="w-5 h-5 mr-2" />
                    Logout
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm Logout</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to log out? You’ll be redirected to the home page.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { dispatch(logout()); navigate("/"); }}>
                      Logout
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>}
              {isEditing && (
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full sm:w-auto px-6 py-3 text-base sm:text-lg"
                >
                  <X className="w-5 h-5 mr-2" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Personal & Address Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
        {/* Personal Info */}
        <Card className="p-4 sm:p-6">
          <CardHeader className="p-0 pb-4 sm:pb-6">
            <CardTitle className="flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl">
              <User className="w-6 h-6 sm:w-7 sm:h-7" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4 sm:space-y-6">
            <div>
              <Label className="flex items-center gap-2 text-sm sm:text-lg mb-2">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                Username
              </Label>
              <Input
                type="text"
                value={profile.userName}
                disabled={!isEditing}
                onChange={(e) => setProfile({ ...profile, userName: e.target.value })}
                className="h-10 sm:h-12 text-base sm:text-lg"
              />
            </div>
            <div>
              <Label className="flex items-center gap-2 text-sm sm:text-lg mb-2">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                Email
              </Label>
              <Input
                type="email"
                value={profile.email}
                disabled={!isEditing}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="h-10 sm:h-12 text-base sm:text-lg"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2 text-sm sm:text-lg mb-2">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                Phone Number
              </Label>
              <Input
                value={profile.phoneNumber}
                disabled={!isEditing}
                onChange={(e) => setProfile({ ...profile, phoneNumber: e.target.value })}
                className="h-10 sm:h-12 text-base sm:text-lg"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address Info */}
        <Card className="p-4 sm:p-6">
          <CardHeader className="p-0 pb-4 sm:pb-6">
            <CardTitle className="flex items-center gap-2 sm:gap-3 text-xl sm:text-2xl">
              <MapPin className="w-6 h-6 sm:w-7 sm:h-7" />
              Address Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 space-y-4 sm:space-y-6">
            <div>
              <Label className="text-sm sm:text-lg mb-2 block">Address</Label>
              <Input
                value={profile.address}
                disabled={!isEditing}
                onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                className="h-10 sm:h-12 text-base sm:text-lg"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <Label className="text-sm sm:text-lg mb-2 block">City</Label>
                <Input
                  value={profile.city}
                  disabled={!isEditing}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  className="h-10 sm:h-12 text-base sm:text-lg"
                />
              </div>
              <div>
                <Label className="text-sm sm:text-lg mb-2 block">Province</Label>
                <Input
                  value={profile.province}
                  disabled={!isEditing}
                  onChange={(e) => setProfile({ ...profile, province: e.target.value })}
                  className="h-10 sm:h-12 text-base sm:text-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <Label className="text-sm sm:text-lg mb-2 block">Country</Label>
                <Input
                  value={profile.country}
                  disabled={!isEditing}
                  onChange={(e) => setProfile({ ...profile, country: e.target.value })}
                  className="h-10 sm:h-12 text-base sm:text-lg"
                />
              </div>
              <div>
                <Label className="text-sm sm:text-lg mb-2 block">Postal Code</Label>
                <Input
                  value={profile.postalCode}
                  disabled={!isEditing}
                  onChange={(e) => setProfile({ ...profile, postalCode: e.target.value })}
                  className="h-10 sm:h-12 text-base sm:text-lg"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {stats.map((stat, index) => (
          <Card
            key={index}
            className="hover:shadow-lg transition-all duration-300"
          >
            <CardContent className="p-6 sm:p-8 text-center">
              <div className="flex justify-center mb-3 sm:mb-4">{stat.icon}</div>
              <div className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                {stat.value}
              </div>
              <div className="text-sm sm:text-lg text-gray-600">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
